
import { AxiosRequestConfig } from 'axios';
import { GetLeads, GetLeadReqType } from 'service/apis/Opportunities';
import { getRelativeDate } from 'pages/Opportunities/utils/featureRule';
import { useQuery } from 'react-query';

interface UseLeadsDataParams {
  type: string;
  search: string;
  statusFilter: string[];
  stageFilter: string[];
  productFilter: string[];
  channelFilter: string[];
  enabled?: boolean;
}

export const useLeadsData = ({
  type,
  search,
  statusFilter,
  stageFilter,
  productFilter,
  channelFilter,
  enabled = true,
}: UseLeadsDataParams) => {
  return useQuery({
    queryKey: [
      'leads',
      type,
      search,
      statusFilter,
      stageFilter,
      productFilter,
      channelFilter,
    ],
    queryFn: async ({ signal }) => {
      const payload: AxiosRequestConfig<GetLeadReqType> = {
        signal,
        params: {
          type: 'retail',
          productType: type,
          searchQuery: search,
          status: statusFilter,
          stage_tag: stageFilter,
          product_id: productFilter,
          channel_of_interaction_id: channelFilter,
        },
      };

      const response = await GetLeads(payload);
      
      // Transform data
      const transformedData = response?.data
        ?.map((item: any) => {
          let customer = "N/A";
          let finalCustomerName = "N/A";
          const remitCustomerName =
            item?.pipelineType === "coorporate"
              ? item?.company?.company_name || "N/A"
              : item?._contacts?.[0]?.contactName ||
                item?._customers?.full_name ||
                "N/A";

          if (item?.coorporate_company_id) {
            customer = item?._customers?.entity_info?.entity_name || "N/A";
          } else if (item?.customers_id) {
            customer = item?.customer_detail?.full_name
              ? item?.customer_detail?.full_name
              : item?._customers?.full_name || "N/A";
          } else {
            customer = item?._contacts?.[0]?.contactName || "N/A";
          }

          if (type === "Forex") {
            finalCustomerName = customer;
          } else {
            finalCustomerName = remitCustomerName;
          }

          // for product name and productSubcategory
          let productName = "";
          let productSubCategory = "";

          if (item?._product) {
            productName = item._product.name;
            productSubCategory = item._product.subCategory;
          } else {
            const productNameSet: Set<string> = new Set();
            const subCategorySet: Set<string> = new Set();

            item?.product_details?.forEach((prod: any) => {
              if (prod?.name) productNameSet.add(prod.name);
              if (prod?.subCategory) subCategorySet.add(prod.subCategory);
            });

            productName = Array.from(productNameSet).join(", ");
            productSubCategory = Array.from(subCategorySet).join(", ");
          }

          return {
            id: item?.id,
            customer: finalCustomerName,
            product: productName,
            productSubCategory: productSubCategory,
            channel: item?._channel_of_interaction?.[0]?.Channel,
            createdBy: item?._createdBy?.fname,
            assignedTo: item?._assignedTo?.fname,
            assignedToCity: item?._assignedTo?.city,
            leadStatus: item?.reasonToClose,
            createdAt: getRelativeDate(item?.created_at),
            contactNumber: item?._contacts?.[0]?.contactNumber || "N/A",
            channelType: item?._channel_of_interaction?.[0]?.Type,
            channelSource: item?._channel_of_interaction?.[0]?.source,
            latestQuotationPDF: item?.interactions_id?.at(-1)?.quotation_pdf,
            requestQuote_Type: item?.quotation_req,
            req_book_rate: item?.req_book_rate,
            purpose_id: item?.purpose_id,
            channel_of_interaction_id: item?._channel_of_interaction?.[0]?.id,
            contacts_id: item?.contacts_id,
            doc_status: item?.doc_status,
            referredBy:
              item?.refferedBy_staff?.fname ||
              item?.refferedBy_partner?.name ||
              item?.refferedBy_contacts?.name ||
              "N/A",
            pipelineType: item?.pipelineType,
            coorporateState: item?.company?.state,
            coorporateCity: item?.company?.city,
            corporate_details: item?.corp_lead_detail || {},
            stage_tag: item?.stage_tags,
            walkiInBranch: item?.walkin_branch?.city,
          };
        })
        .sort((a: any, b: any) => a.customer.localeCompare(b.customer)) || [];

      return transformedData;
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 2,
  });
};